import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import PropTypes from 'prop-types'

import {
	Space,
	ConfigProvider,
	Form,
	Input,
	Card,
	Select,
	Row,
	Col,
	Tag,
	Tooltip,
	Button,
	InputNumber,
	DatePicker
} from 'antd'
import {
	WarningOutlined,
	InfoCircleOutlined,
	HistoryOutlined,
	EyeOutlined,
	SettingOutlined
} from '@ant-design/icons'

import Title from 'antd/es/typography/Title'
import { runInAction, toJS } from 'mobx'
import dayjs from 'dayjs'
import { isMobile } from 'react-device-detect'

import { IS_NEW } from '../../../config'
import {
	actLoadWorkflow,
	actSaveWorkflow,
	requireWorkflowById
} from '../../../stores/workflows/actions'
import { useStores } from '../../../cls/stores/MobXProvider'

// Reuse some common form components if applicable
import { CommentsInput, SubmitButton } from '../../../components/Form'
import {
	Features,
	FormatedTimestamp,
	UserString
} from '../../../components/Shared'
import AllowedCategories from '../../../components/Shared/AllowedCategories'
import { getTourArrayByKeys } from '../../../stores/tour/config'
import '../../../design/styles/patterns.css'
import {
	GeneralContentWrapper,
	GeneralTourTitle
} from '../../../components/Shared/GeneralComponents'
import ProgressModal from '../../../components/Form/ProgressModal'
import { sendNotification } from '../../../stores/notifications/action'
import { actLoadUsers } from '../../../stores/ui-lst-users/actions'
import ImportExport from '../../../components/Shared/ImportExport'
import { useSmartTranslation } from '../../../cls/TranslationContext'

/**
 * @function WorkflowsEdit
 * @description Main component for editing or viewing a single workflow item.
 * Handles form state, validation, permission checks, conditional features (steps, comments),
 * notifications, and tour integration.
 *
 * @param {object} props
 * @param {boolean} props.viewOnly - Whether the component is rendered in read-only mode.
 * @returns {JSX.Element}
 */
const WorkflowsEdit = ({ viewOnly = false }) => {
	const { t } = useSmartTranslation()
	const navigate = useNavigate()
	const { itmId } = useParams()
	const { uiLstWorkflows, users, tourStore, priorityOptions, uiLstUsers } =
		useStores()
	const { loggedInUserId, loggedInUser } = users
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin

	// Get the workflow item from the store.
	const _itm = requireWorkflowById(itmId)
	console.log('--- WorkflowsEdit', _itm, toJS(_itm))

	const selfPermission =
		_itm.createdBy === loggedInUser.user_id || isAdmin || isSuperAdmin

	// If user does not have permission and this isn’t a new workflow, set viewOnly mode.
	if (!selfPermission && _itm.loaded !== IS_NEW) {
		viewOnly = true
	}

	// Generate initial form values for a workflow.
	/**
	 * @function generateInitialFormValues
	 * @description Constructs initial values for the AntD form using the workflow data from the store.
	 *
	 * @returns {object} Initial values for form fields.
	 */
	const generateInitialFormValues = () => ({
		name: _itm.name,
		workflow_type: _itm.workflow_type,
		description: _itm.description || '',
		due_date: _itm.due_date ? dayjs.unix(_itm.due_date) : null,
		for_user_id: _itm.for_user_id || loggedInUserId,
		assignedUserIds: _itm.assignedUserIds || [],
		priority: _itm.priority || 0,
		effortEstimate: _itm.effortEstimate || 0,
		status: _itm.status || 'draft',
		steps: toJS(_itm.steps?.list) || [],
		comments: toJS(_itm.comments.list) || []
	})

	// Load users on mount.
	useEffect(() => {
		actLoadUsers().catch(console.error)
	}, [loggedInUserId])

	// Determine notification type.
	const notificationKind = _itm.loaded === IS_NEW ? 'new' : 'update'
	const tableEntity = 'workflows'

	const [forUser, setForUser] = useState({})
	useEffect(() => {
		setForUser(users.requireUserById(_itm.for_user_id))
	}, [_itm])

	// Save workflow function.
	/**
	 * @function _doSave
	 * @description Handles form submission. Applies changes to the MobX store,
	 * persists the updated workflow, and optionally sends a notification if the responsible user differs.
	 *
	 * @param {object} vals - Form values after submission.
	 */
	const _doSave = (vals) => {
		if (vals !== null) {
			runInAction(() => {
				!_itm.createdBy && _itm.setCreator(loggedInUserId)
				_itm.loaded === IS_NEW && _itm.resetLastFullLoad()

				_itm.setName(vals.name)
				_itm.setWorkflowType(vals.workflow_type)
				_itm.setDescription(vals.description)
				_itm.setDueDate(vals.due_date ? vals.due_date.unix() : null)
				_itm.setForUserId(vals.for_user_id)
				_itm.setAssignedUserIds(vals.assignedUserIds)
				_itm.setPriority(vals.priority)
				_itm.setEffortEstimate(vals.effortEstimate)
				_itm.setStatus(vals.status)
				isSuperAdmin && _itm.setCreatedBy(vals.createdBy)

				// Save steps and comments.
				_itm.setSteps(vals.steps)
				_itm.setComments(vals.comments)
			})
			actSaveWorkflow(itmId, toJS(_itm.full))
				.then(() => {
					if (_itm.createdBy !== _itm?.for_user_id) {
						sendNotification(
							loggedInUser,
							forUser?.email,
							_itm?.for_user_id,
							tableEntity,
							itmId,
							notificationKind
						)
					}
					navigate('/workflows')
				})
				.catch(console.error)
		}
	}

	const [form] = Form.useForm()

	// Update form when the workflow item is modified.
	useEffect(() => {
		form.setFieldsValue(generateInitialFormValues())
	}, [_itm.modified])

	const [formIsDisabled, setFormIsDisabled] = useState(
		!!_itm.to_delete ?? viewOnly
			? true
			: isAdmin
			? false
			: _itm.status !== 'draft'
	)

	useEffect(() => {
		setFormIsDisabled(
			viewOnly
				? true
				: !!_itm.to_delete ??
						(isAdmin ? false : _itm.status !== 'draft')
		)
		form.setFieldsValue(generateInitialFormValues())
	}, [_itm.to_delete, viewOnly])

	const [features, setFeatures] = useState({
		steps: toJS(_itm.steps?.list)?.length > 0,
		comments: viewOnly ? true : toJS(_itm.comments.list)?.length > 0
	})

	// If a new workflow is created by an admin, default status to "active".
	useEffect(() => {
		if (_itm.loaded === IS_NEW && (isAdmin || isSuperAdmin)) {
			form.setFieldsValue({
				status: 'active'
			})
		}
	}, [isAdmin, isSuperAdmin])

	/**
	 * @function onFeaturesChange
	 * @description Triggered when the features toggle (steps/comments) is changed. Clears corresponding form fields if disabled.
	 *
	 * @param {object} features - Feature toggles { steps: boolean, comments: boolean }.
	 */
	const onFeaturesChange = (features) => {
		if (!features.steps) {
			form.setFieldsValue({
				steps: []
			})
		}
		if (!features.comments) {
			form.setFieldsValue({
				comments: []
			})
		}
	}

	// A function to trigger a progress modal.
	/**
	 * @function onSetProgressChange
	 * @description Opens the progress modal for editing workflow progress manually.
	 */
	const onSetProgressChange = () => {
		runInAction(() => {
			uiLstWorkflows.setModal('workflow-progress', {
				visible: true,
				itmId: itmId
			})
		})
	}

	const tourFeatures =
		!viewOnly && !formIsDisabled
			? getTourArrayByKeys('features', ['all'])
			: []
	const tourForm = getTourArrayByKeys('form', [
		'section',
		'name',
		'workflow_type',
		'for_user_id',
		'assignedUserIds',
		'status',
		'priority',
		'effortEstimate',
		'due_date'
	])
	/**
	 * @function startTour
	 * @description Initiates the guided tour for the workflow edit view, including features and form sections.
	 */
	const startTour = () => {
		tourStore.startTour('WorkflowsEdit-tour', [
			...tourFeatures,
			...getTourArrayByKeys('info', ['name', 'modified']),
			...getTourArrayByKeys('form', ['section'])
		])
	}
	/**
	 * @function startTourForm
	 * @description Initiates the guided tour for only the form fields of the workflow.
	 */
	const startTourForm = () => {
		tourStore.startTour('WorkflowsEdit-tour', [...tourForm])
	}

	/**
	 * @function _withValuesExecute
	 * @description Executes feature toggles based on the imported values and triggers related field UI changes.
	 *
	 * @param {object} values - Imported values to apply.
	 */
	const _withValuesExecute = (values) => {
		Object.keys(features).forEach((feature) => {
			if (values[feature]) {
				document
					.getElementById('target-features-' + feature)
					.querySelector('input')
					.click()
			}
		})
	}

	return (
		<GeneralContentWrapper>
			<GeneralTourTitle
				titleProps={{ text: t('_workflow') }}
				buttonProps={{ onClick: startTour }}
				rowProps={{ justify: 'space-between' }}
			>
				{!!_itm.to_delete && (
					<Tooltip
						title={
							<>
								{t('single.delete-marked')}{' '}
								<FormatedTimestamp
									timestamp={_itm.toDeleteTs}
								/>
							</>
						}
					>
						<Tag color="red" icon={<WarningOutlined />}>
							{t('single.delete-marked')}{' '}
						</Tag>
					</Tooltip>
				)}
			</GeneralTourTitle>

			{!viewOnly && !formIsDisabled && (
				<Features
					features={features}
					setFeatures={(val) => {
						setFeatures(val)
						onFeaturesChange(val)
					}}
				/>
			)}
			<Card
				title={
					<span id="target-info-name">
						{viewOnly ? (
							<Tooltip title={t('single.workflow.view-details')}>
								<EyeOutlined
									className="no-print"
									id="target-mode"
								/>{' '}
								{_itm?.name}
							</Tooltip>
						) : _itm.loaded === IS_NEW ? (
							t('__create-new%_workflow')
						) : (
							<Tooltip title={t('single.edit')}>
								<SettingOutlined /> {_itm?.name}
							</Tooltip>
						)}
					</span>
				}
				style={{ width: '100%' }}
				extra={
					<div id="target-info-modified">
						<Tooltip title={t('single.last-updated')}>
							<FormatedTimestamp
								timestamp={_itm.modifiedTs}
								formatedAs="DD.MM.YYYY hh:mm"
							/>{' '}
							<HistoryOutlined />
						</Tooltip>
					</div>
				}
			>
				<ProgressModal
					type="workflow"
					modalKey="workflow-progress"
					uiStore={uiLstWorkflows}
					requireItemById={requireWorkflowById}
					loadItm={(id) => actLoadWorkflow(id)}
					saveItem={(itemId, itemObject) => {
						actSaveWorkflow(itemId, itemObject).then((res) =>
							console.log('Progress saved', res)
						)

						form.setFieldsValue({
							progress: _itm.progress
						})

						_itm.steps?.list.length > 0 &&
							form.setFieldsValue({
								steps: toJS(_itm.steps.list)
							})
						if (_itm.createdBy !== _itm?.for_user_id) {
							sendNotification(
								loggedInUser,
								forUser?.email,
								_itm?.for_user_id,
								tableEntity,
								itmId,
								'progress'
							)
						}
					}}
				/>
				<ConfigProvider>
					<Form
						layout="vertical"
						form={form}
						name="workflow"
						initialValues={generateInitialFormValues()}
						onFinish={_doSave}
						className={viewOnly && 'view-only-styles'}
					>
						<Row gutter={[50, 50]}>
							<Col
								id="target-form"
								className="section-pattern-dots"
								span={24}
							>
								<Space
									style={{ width: '100%' }}
									direction="vertical"
								>
									<GeneralTourTitle
										align="end"
										titleProps={{
											text: t(
												'single.general-information'
											),
											h: Title,
											level: 5
										}}
										buttonProps={{
											onClick: startTourForm,
											disabled: false,
											style: { marginBottom: 4 }
										}}
									/>
									<Form.Item
										label={t('single.name')}
										tooltip={{
											title: t(
												'_unique.masculine%single.name'
											),
											icon: <InfoCircleOutlined />
										}}
										name="name"
										rules={[
											{
												required: true,
												message: t(
													'__provide.please%single.name'
												)
											}
										]}
									>
										<Input
											maxLength={100}
											showCount={!viewOnly}
											disabled={formIsDisabled}
										/>
									</Form.Item>
									<Row gutter={20}>
										<Col
											span={12}
											id="target-form-workflow_type"
										>
											<Form.Item
												label={t('__type%_workflow')}
												tooltip={{
													title: t('single.type'),
													icon: <InfoCircleOutlined />
												}}
												name="workflow_type"
												rules={[
													{
														required: true,
														message: t(
															'__select.please%single.type'
														)
													}
												]}
											>
												<AllowedCategories
													disabled={formIsDisabled}
												/>
											</Form.Item>
										</Col>
										<Col
											span={12}
											id="target-form-due_date"
										>
											<Form.Item
												label={t('single.due-date')}
												tooltip={{
													title: t(
														'__select.item%single.due-date'
													),
													icon: <InfoCircleOutlined />
												}}
												name="due_date"
												rules={[
													{
														required: false
													}
												]}
											>
												<DatePicker
													placeholder={t(
														'__select.item%single.date'
													)}
													disabled={formIsDisabled}
													style={{ width: '100%' }}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Form.Item
										label={t('single.description')}
										tooltip={{
											title: t(
												'__provide.please%single.description'
											),
											icon: <InfoCircleOutlined />
										}}
										name="description"
									>
										<Input.TextArea
											rows={3}
											disabled={formIsDisabled}
										/>
									</Form.Item>
									<Row gutter={20}>
										<Col
											span={12}
											id="target-form-for_user_id"
										>
											<Form.Item
												label={t(
													'single.responsible-user'
												)}
												tooltip={{
													title: t(
														'single.assign-responsible'
													),
													icon: <InfoCircleOutlined />
												}}
												name="for_user_id"
												rules={[
													{
														required: true,
														message: t(
															'single.assign-responsible'
														)
													}
												]}
											>
												<Select
													disabled={formIsDisabled}
													placeholder={t(
														'__select.item%_user'
													)}
													allowClear
													style={{
														minWidth: '200px'
													}}
													options={[
														...[loggedInUserId],
														...uiLstUsers.ids
													].map((userId) => ({
														label: (
															<UserString
																key={userId}
																userId={userId}
																path={['name']}
															/>
														),
														value: userId
													}))}
												/>
											</Form.Item>
										</Col>
										<Col
											span={12}
											id="target-form-assignedUserIds"
										></Col>
									</Row>
									<Row gutter={20}>
										<Col
											span={12}
											id="target-form-priority"
										>
											<Form.Item
												label={t(
													'single.filter.priority-desc'
												)}
												tooltip={{
													title: t('single.priority'),
													icon: <InfoCircleOutlined />
												}}
												name="priority"
												rules={[
													{
														required: true,
														message: t(
															'__select.please%single.priority'
														)
													}
												]}
											>
												<Select
													disabled={formIsDisabled}
													placeholder={t(
														'__select.item%single.priority'
													)}
													allowClear
													style={{
														minWidth: '200px'
													}}
													options={priorityOptions}
												/>
											</Form.Item>
										</Col>
										<Col
											span={12}
											id="target-form-effortEstimate"
										>
											<Form.Item
												label={t(
													'single.effort.estimate'
												)}
												tooltip={{
													title: t(
														'single.estimated-hours-workflow-xxxx'
													),
													icon: <InfoCircleOutlined />
												}}
												name="effortEstimate"
												rules={[
													{
														required: true,
														message: t(
															'__provide.please%single.effort-estimate'
														)
													}
												]}
											>
												<InputNumber
													addonBefore={t(
														'_hour.multiple'
													)}
													min={0}
													max={100}
													step={1}
													disabled={formIsDisabled}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Row gutter={[10, 10]}>
										<Col span={12}>
											<Form.Item
												label={t('single.status')}
												tooltip={{
													title: t(
														'__select.please%single.status'
													),
													icon: <InfoCircleOutlined />
												}}
												name="status"
											>
												<Select
													disabled={formIsDisabled}
													placeholder={t(
														'__select.item%single.status'
													)}
													allowClear
													options={[
														{
															label: t(
																'single.draft'
															),
															value: 'draft'
														},
														{
															label: t(
																'single.active'
															),
															value: 'active'
														},
														{
															label: t(
																'single.completed'
															),
															value: 'completed'
														}
													]}
												/>
											</Form.Item>
										</Col>
										<Col
											span={12}
											id="target-form-progress"
										>
											<Row align="bottom">
												<Col>
													<Form.Item
														label={t(
															'single.progress'
														)}
														tooltip={{
															title: t(
																'single.progress-indicator-description'
															),
															icon: (
																<InfoCircleOutlined />
															)
														}}
														name="progress"
														rules={[
															{
																required: true,
																message:
																	t(
																		'single.progress'
																	)
															}
														]}
													>
														<Input
															disabled={
																formIsDisabled ||
																_itm.loaded !==
																	IS_NEW
															}
														/>
													</Form.Item>
												</Col>
												<Col>
													{viewOnly &&
														(selfPermission ||
															users.canUser(
																'write',
																tableEntity
															)) && (
															<Col>
																<Form.Item>
																	<Button
																		className="no-print"
																		type="primary"
																		onClick={(
																			e
																		) => {
																			e.preventDefault()
																			onSetProgressChange()
																		}}
																		ghost
																	>
																		{t(
																			'single.set-progress'
																		)}
																	</Button>
																</Form.Item>
															</Col>
														)}
												</Col>
											</Row>
										</Col>
									</Row>
								</Space>
							</Col>
						</Row>
						<Row gutter={[50, 50]}>
							{features.steps && (
								<Col
									span={
										features.comments && !isMobile ? 12 : 24
									}
								>
									<Title level={5}>
										{t('_workflow.steps')}
									</Title>
									<Form.Item
										label={t('single.steps')}
										tooltip={{
											title: t(
												'single.outline-steps-or-stages'
											),
											icon: <InfoCircleOutlined />
										}}
										name="steps"
									>
										<Input.TextArea
											rows={4}
											disabled={formIsDisabled}
										/>
									</Form.Item>
								</Col>
							)}
							{features.comments && (
								<Col
									span={features.steps && !isMobile ? 12 : 24}
								>
									<Title level={5}>
										{t('_comment.multiple')}
									</Title>
									<Form.Item
										label={t('_comment.multiple')}
										tooltip={{
											title: t(
												'single.additional-information-or-notes'
											),
											icon: <InfoCircleOutlined />
										}}
										name="comments"
									>
										<CommentsInput
											createdBy={loggedInUserId}
											disabled={
												!selfPermission &&
												!users.canUser(
													'write',
													tableEntity
												)
											}
										/>
									</Form.Item>
								</Col>
							)}
						</Row>
						<Row
							gutter={[10, 10]}
							align="bottom"
							className="no-print"
						>
							<Col>
								<SubmitButton
									disabled={
										!selfPermission &&
										!users.canUser('write', tableEntity)
									}
								>
									{_itm.loaded === IS_NEW
										? t('__create%_workflow')
										: t('__update%_workflow')}
								</SubmitButton>
							</Col>
							{!viewOnly && (
								<Col>
									<Button
										type="default"
										onClick={() => navigate(-1)}
									>
										{t('single.cancel')}
									</Button>
								</Col>
							)}
							{viewOnly && (
								<Col>
									<Button
										type="default"
										onClick={() => window.print()}
									>
										{t('single.download')}
									</Button>
								</Col>
							)}
							{!viewOnly && (
								<Col
									style={{ marginLeft: 'auto' }}
									className="no-print"
								>
									<Space style={{ width: '100%' }}>
										<ImportExport
											form={form}
											withValuesExecute={
												_withValuesExecute
											}
										/>
									</Space>
								</Col>
							)}
						</Row>
					</Form>
				</ConfigProvider>
			</Card>
		</GeneralContentWrapper>
	)
}

WorkflowsEdit.propTypes = {
	viewOnly: PropTypes.bool
}

export default observer(WorkflowsEdit)
